import { Cross1Icon, ExitIcon, GearIcon, HamburgerMenuIcon } from '@radix-ui/react-icons';
import { clsx } from 'clsx';
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ChangeEvent, useCallback, useRef, useState } from 'react';

import { useFeatureFlags } from '@src/hooks/useFeatureFlags';

import { IconButton } from './IconButton';
import { NavigationLink } from './NavigationLink';
import { NavigationSectionTitle } from './NavigationSectionTitle';
import { SiteLogo } from './SiteLogo';
import { NavigationSection } from './types';

import {
  SectionsContainer,
  StyledDialog,
  StyledDialogDisclosure,
  StyledNav,
  navigationMenuLink,
} from './mobileNavigation.css';

export interface MobileNavigationProps {
  /** The configuration for the displayed sections and links. */
  sections: readonly NavigationSection[];
  /** The path that clicking on the Ocula logo links to. */
  homePath: string;
  /** Callback for when the user clicks the logout button. */
  onLogout: () => void;
  /** Callback for when the user clicks the settings button. */
  onSettings: () => void;
}

export function MobileNavigation({ sections, homePath, onLogout, onSettings }: MobileNavigationProps) {
  const [dialogState, setDialogState] = useState<'open' | 'closing' | 'closed'>('closed');
  const {
    values: { showFeatureFlagSettings },
  } = useFeatureFlags();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openDialog = () => {
    if (!dialogRef?.current?.open) {
      dialogRef?.current?.showModal();
      setDialogState('open');
    }
  };

  const onCloseFinish = useCallback(() => {
    dialogRef?.current?.close();
    setDialogState('closed');
  }, []);

  const onClose = useCallback(() => {
    setDialogState('closing');
    setTimeout(() => {
      onCloseFinish();
    }, 250);
  }, [onCloseFinish]);

  const onEsc = useCallback(
    (e: ChangeEvent<HTMLDialogElement>) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  const onClick = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
    ({ target }: any) => {
      const { current: el } = dialogRef;
      if (target === el) {
        onClose();
      }
    },
    [onClose],
  );
  return (
    <>
      <nav className={StyledNav} aria-label="Site navigation">
        <button
          className={StyledDialogDisclosure}
          aria-expanded={false}
          aria-label={dialogState === 'open' ? 'Close site navigation' : 'Open site navigation'}
          aria-controls="mobileNavDialog"
          onClick={openDialog}
          type="button"
        >
          {dialogState === 'open' ? <Cross1Icon /> : <HamburgerMenuIcon />}
        </button>

        <SiteLogo homePath={homePath} compact />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {showFeatureFlagSettings && <IconButton onClick={onSettings} label="Settings" icon={<GearIcon />} />}
          <IconButton onClick={onLogout} label="Logout" icon={<ExitIcon />} />
        </div>
      </nav>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: TODO: fix */}
      <dialog
        aria-label="Navigation"
        id="mobileNavDialog"
        ref={dialogRef}
        onClick={onClick}
        onCancel={onEsc}
        className={clsx(StyledDialog, dialogState === 'closing' ? 'modal-closing' : undefined)}
      >
        <div className={SectionsContainer}>
          {sections.map(({ sectionLabel, links }) => (
            <section key={sectionLabel}>
              <NavigationSectionTitle title={sectionLabel} compact={false} />
              {links.map((link) => (
                <NavigationLink
                  key={link.to}
                  link={link}
                  compact={false}
                  onClick={() => dialogRef?.current?.close()}
                  disabled={link.disabled}
                  className={navigationMenuLink}
                />
              ))}
            </section>
          ))}
        </div>
      </dialog>
    </>
  );
}
